
export default function ServicesDetailSection() {
  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Complete Small Business <span className="text-teal-600">Website Solutions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We provide everything your El Paso small business needs to succeed online, from custom design 
            to ongoing maintenance and optimization.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-palette-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Custom Website Design</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Every small business is unique, which is why we create completely custom website designs tailored 
              to your brand, industry, and target audience. No templates, no cookie-cutter solutions.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Brand-aligned visual design</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>User experience optimization</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Industry-specific layouts</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Professional photography integration</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-smartphone-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Mobile-First Development</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              With over 60% of web traffic coming from mobile devices, your website must work perfectly on 
              smartphones and tablets. We build mobile-first to ensure optimal performance across all devices.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Responsive design for all screen sizes</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Touch-friendly navigation</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Fast loading on mobile networks</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Mobile-optimized contact forms</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-search-2-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">SEO Optimization</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Your beautiful website needs to be found by potential customers. We build every site with 
              comprehensive SEO optimization to help your El Paso business rank higher in Google searches.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Local El Paso SEO targeting</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Keyword research and optimization</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Google My Business integration</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-600"></i>
                <span>Schema markup and technical SEO</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-12 shadow-lg mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Essential Features for Small Business Success
              </h3>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Every small business website we create includes the core features necessary to attract customers, 
                generate leads, and grow your business online. These aren't add-ons or extras—they're included 
                in every project because they're essential for success.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Contact Forms</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Click-to-Call Buttons</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Location & Hours</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-star-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Customer Reviews</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="ri-image-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Photo Galleries</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-share-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Social Media Integration</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-calendar-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">Online Scheduling</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                    <span className="font-semibold text-gray-900">SSL Security</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/7e7834176f6e2f3bc3070e03c011bd24.jpg"
                alt="Essential small business website features and functionality"
                className="w-full h-80 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-speed-up-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">Lightning Fast</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              Optimized for speed with page load times under 3 seconds to keep visitors engaged.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-20 h-20 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-lock-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">Secure & Safe</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              SSL certificates, secure hosting, and regular backups protect your website and customer data.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-20 h-20 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-settings-3-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">Easy to Update</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              User-friendly content management system lets you update your website without technical knowledge.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-20 h-20 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-line-chart-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">Analytics Ready</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              Built-in tracking tools help you understand your visitors and measure your website's performance.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
